// @flow

import React from 'react'
import styled, { css } from 'styled-components'
import Colors from 'components/styles/colors'
import { getStyleUnit } from 'components/styles/helpers'
import { borderRadius } from 'frontend_core/dist/styles/variables'
import { mediaMin } from 'frontend_core/dist/styles/media'
import Icon from 'components/atoms/Icon'

const color = {
  colorType: {
    blue: {
      main: Colors.blue,
      sub: Colors.blueBdColor,
    },
    green: {
      main: Colors.green,
      sub: Colors.greenBdColor,
    },
    pink: {
      main: Colors.stayPink,
      sub: Colors.stayPinkBdColor,
    },
    gray: {
      main: Colors.gray9,
      sub: Colors.gray7,
    },
    grayB: {
      main: Colors.grayB,
    },
    white: {
      main: Colors.white,
      sub: Colors.white,
    },
    onlyBorderGray: {
      main: Colors.gray9,
      sub: Colors.black,
    },
  },
}

const ButtonWrapper = styled.div`
  ${({ isRight }) =>
    isRight &&
    css`
      text-align: right;
    `}
  ${({ isLeft }) =>
    isLeft &&
    css`
      text-align: left;
    `}
  ${({ isCenter }) =>
    isCenter &&
    css`
      text-align: center;
    `}
  ${({ buttonWrapperMargin }) =>
    buttonWrapperMargin &&
    css`
      margin: ${buttonWrapperMargin};
    `}
`

const defaultHeight = 30

const ButtonComponent = styled.div`
  display: inline-block;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
  border-radius: ${borderRadius}px;
  outline: none;
  cursor: pointer;
  ${({ colorType }) =>
    colorType &&
    css`
    color: ${Colors.white};
    background-color: ${color.colorType[colorType].main};
    &:hover {
      background-color: ${color.colorType[colorType].sub};
    }
    ${({ isBorder }) =>
      isBorder &&
      css`
        border: 1px solid ${color.colorType[colorType].sub};
      `}
    ${({ isBottomBorder }) =>
      isBottomBorder &&
      css`
        border-bottom: 1px solid ${color.colorType[colorType].sub};
      `}
    ${({ isOnlyBorder }) =>
      isOnlyBorder &&
      css`
        color: ${color.colorType[colorType].sub};
        background-color: ${Colors.white};
        border: 1px solid ${color.colorType[colorType].main};
        font-weight: normal;
        &:hover {
          background-color: ${Colors.grayF};
        }
      `}
    ${({ disabled }) =>
      disabled &&
      css`
        background-color: ${Colors.grayE};
        color: ${Colors.grayD};
        &:hover {
          background-color: ${Colors.grayE};
          cursor: not-allowed;
        }
      `}
  `}
  ${({ paddingSp }) =>
    paddingSp &&
    css`
      padding: ${paddingSp};
    `}
  ${({ paddingPc }) =>
    paddingPc &&
    css`
      ${mediaMin.phone`
        padding: ${paddingPc};
      `};
    `}
  ${({ buttonWidthSp }) =>
    buttonWidthSp &&
    css`
      width: ${getStyleUnit(buttonWidthSp)};
    `}
  ${({ buttonWidthPc }) =>
    buttonWidthPc &&
    css`
      ${mediaMin.phone`
      width: ${getStyleUnit(buttonWidthPc)};
    `};
    `}
  ${({ fullWidthSp }) =>
    fullWidthSp &&
    css`
      width: 100%;
    `}
  ${({ fullWidthPc }) =>
    fullWidthPc &&
    css`
      ${mediaMin.phone`
      width: 100%;
    `};
    `}
  ${({ buttonHeightSp }) =>
    buttonHeightSp &&
    css`
      height: ${buttonHeightSp}px;
    `}
  ${({ buttonHeightPc }) =>
    buttonHeightPc &&
    css`
      ${mediaMin.phone`
      height: ${buttonHeightPc}px;
    `};
    `}
  ${({ isCircle, buttonHeight }) =>
    isCircle &&
    css`
      border-radius: ${(buttonHeight || defaultHeight) / 2}px;
    `}
  ${({ buttonMarginSp }) =>
    buttonMarginSp &&
    css`
      margin: ${buttonMarginSp};
    `}
  ${({ buttonMarginPc }) =>
    buttonMarginPc &&
    css`
      ${mediaMin.phone`
      margin: ${buttonMarginPc};
    `};
    `}
  ${({ fontSizeSp }) =>
    fontSizeSp &&
    css`
      font-size: ${fontSizeSp}px;
    `}
  ${({ fontSizePc }) =>
    fontSizePc &&
    css`
      ${mediaMin.phone`
          font-size: ${fontSizePc}px;
        `};
    `}
  ${({ fontWeight }) =>
    fontWeight &&
    css`
      font-weight: bold;
    `}
  ${({ lineHeightSp }) =>
    lineHeightSp &&
    css`
      line-height: ${lineHeightSp}px;
    `}
  ${({ lineHeightPc }) =>
    lineHeightPc &&
    css`
      ${mediaMin.phone`
      line-height: ${lineHeightPc}px;
    `};
    `}
  ${({ verticalAlign }) =>
    verticalAlign &&
    css`
      vertical-align: ${verticalAlign};
    `}
`

const ButtonInner = styled.span`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`

const AnchorButton = ButtonComponent.withComponent('a')
const SubmitButton = ButtonComponent.withComponent('button')

function getButtonInner(props) {
  return (
    <ButtonInner>
      {props.iconType &&
        props.iconPositionType === 'left' && (
          <Icon
            className={props.iconType}
            positionType={props.iconPositionType}
          />
        )}
      {props.children
        ? props.children
        : props.iconType && (
            <Icon
              className={props.iconType}
              positionType={props.iconPositionType}
            />
          )}
      {props.iconType &&
        props.iconPositionType === 'right' && (
          <Icon
            className={props.iconType}
            positionType={props.iconPositionType}
          />
        )}
    </ButtonInner>
  )
}

function getButton(props) {
  switch (props.tag) {
    case 'anchor':
      return (
        <AnchorButton
          {...props}
          onClick={props.disabled ? null : props.onClick}
        >
          {getButtonInner(props)}
        </AnchorButton>
      )
    case 'button':
      return (
        <SubmitButton
          {...props}
          onClick={props.disabled ? null : props.onClick}
        >
          {getButtonInner(props)}
        </SubmitButton>
      )
    default:
      return (
        <ButtonComponent
          {...props}
          onClick={props.disabled ? null : props.onClick}
        >
          {getButtonInner(props)}
        </ButtonComponent>
      )
  }
}

export type PropTypes = {
  tag?: string,
  href?: string,
  buttonWidthSp?: string,
  buttonWidthPc?: number | string,
  buttonHeightPc?: string,
  buttonHeightSp?: string,
  buttonMarginPc?: string,
  buttonMarginSp?: string,
  buttonWrapperMargin?: string,
  paddingSp?: string,
  paddingPc?: string,
  fullWidthSp?: boolean,
  fullWidthPc?: boolean,
  iconType?: string,
  iconPositionType?: string,
  colorType?: string,
  fontSizePc?: number | string,
  fontSizeSp?: number | string,
  fontWeight?: string,
  lineHeightPc?: string,
  lineHeightSp?: string,
  isBorder?: boolean,
  isBottomBorder?: boolean,
  isOnlyBorder?: boolean,
  isRight?: boolean,
  isLeft?: boolean,
  isCenter?: boolean,
  isCircle: boolean,
  verticalAlign?: number,
  onClick: Function,
  disabled?: ?boolean,
}

export default class ButtonBase extends React.Component<*> {
  props: PropTypes

  render() {
    const { isRight, isLeft, isCenter, buttonWrapperMargin } = this.props

    if (isRight || isLeft || isCenter) {
      return (
        <ButtonWrapper
          isRight={isRight}
          isLeft={isLeft}
          isCenter={isCenter}
          buttonWrapperMargin={buttonWrapperMargin}
        >
          {getButton(this.props)}
        </ButtonWrapper>
      )
    }
    return getButton(this.props)
  }
}



// WEBPACK FOOTER //
// ./src/components/atoms/Button/ButtonBase.jsx